import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ArrowUpIcon } from "@heroicons/react/16/solid";
import { ReactElement, ReactNode, useState } from "react";
import { toast } from "react-toastify";

import {
  generateLiquidTemplate,
  type GenerateLiquidTemplateError,
} from "../../api/index.ts";
import {
  formatValidationError,
  isValidationError,
} from "../../utils/is-validation-error.ts";
import { Button } from "../catalyst/Button.tsx";
import { Text } from "../catalyst/Text.tsx";
import { Textarea } from "../catalyst/Textarea.tsx";
import { Tooltip } from "../Tooltip.tsx";
const DEFAULT_PANEL_CLASSNAME =
  "z-50 w-72 rounded-xl border border-zinc-700 bg-zinc-800 p-3 shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10";

/** Format API error (400 ValidationError or 404/500 Error) for display. */
const formatApiError = (err: GenerateLiquidTemplateError): string => {
  if (isValidationError(err)) {
    return formatValidationError(err);
  }
  return err.message;
};

export interface FloatingAIFormPanelProps {
  isLoading?: boolean;
  jsonContext: string;
  loadingMessage?: string;
  onError?: (errorMessage: string) => void;
  onSuccess: (template: string) => void;
  panelClassName?: string;
  placeholder: string;
  tooltipContent?: string;
  trigger: ReactNode;
  triggerAriaLabel?: string;
  triggerClassName?: string;
}

/**
 * Reusable floating AI prompt panel (textarea + send + loading). Uses Headless UI
 * Popover for positioning and dismiss; form content and noMatch banner are custom.
 */
export const FloatingAIFormPanel = ({
  isLoading,
  jsonContext,
  loadingMessage = "Generating…",
  onError,
  onSuccess,
  panelClassName = DEFAULT_PANEL_CLASSNAME,
  placeholder,
  tooltipContent,
  trigger,
  triggerAriaLabel,
  triggerClassName,
}: FloatingAIFormPanelProps): ReactElement => {
  const [value, setValue] = useState("");
  const [bannerMessage, setBannerMessage] = useState<null | string>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loading = isLoading ?? isSubmitting;

  const handleSubmit = async (close: () => void): Promise<void> => {
    const trimmed = value.trim();
    if (!trimmed || loading) return;
    setBannerMessage(null);
    setIsSubmitting(true);
    try {
      const result = await generateLiquidTemplate({
        body: {
          jsonContext,
          userDescription: trimmed,
        },
      });
      const data = result?.data;
      if (data?.noMatch === true) {
        const errorMessage =
          data.message ?? "No match found for description, please try again.";
        toast.error(errorMessage);
        onError?.(errorMessage);
      } else if (data?.template != null) {
        onSuccess(data.template);
        close();
      }
    } catch (error) {
      console.error("Error generating Liquid template:", error);
      const err = error as GenerateLiquidTemplateError | undefined;
      const errorMessage =
        err != null &&
        typeof err === "object" &&
        typeof (err as GenerateLiquidTemplateError).message === "string"
          ? formatApiError(err as GenerateLiquidTemplateError)
          : "Failed to generate Liquid template";
      toast.error(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const panel = (
    <Popover className="relative">
      <PopoverButton
        aria-label={triggerAriaLabel}
        as={Button}
        className={triggerClassName}
        disabled={loading}
        plain
        size="xs"
        type="button"
      >
        {trigger}
      </PopoverButton>
      <PopoverPanel anchor="bottom start" className={panelClassName}>
        {({ close }: { close: () => void }) => (
          <PanelContent
            bannerMessage={bannerMessage}
            close={close}
            handleSubmit={handleSubmit}
            isLoading={loading}
            loadingMessage={loadingMessage}
            onBannerClear={() => setBannerMessage(null)}
            placeholder={placeholder}
            setValue={setValue}
            value={value}
          />
        )}
      </PopoverPanel>
    </Popover>
  );

  if (tooltipContent) {
    return (
      <Tooltip content={tooltipContent} position="top">
        {panel}
      </Tooltip>
    );
  }
  return panel;
};

interface PanelContentProps {
  bannerMessage: null | string;
  close: () => void;
  handleSubmit: (close: () => void) => Promise<void>;
  isLoading: boolean;
  loadingMessage: string;
  onBannerClear: () => void;
  placeholder: string;
  setValue: (v: string) => void;
  value: string;
}

const PanelContent = ({
  bannerMessage,
  close,
  handleSubmit,
  isLoading,
  loadingMessage,
  onBannerClear,
  placeholder,
  setValue,
  value,
}: PanelContentProps): ReactElement => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    e.stopPropagation();
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit(close);
    }
  };

  return (
    <div className="space-y-2">
      {bannerMessage && (
        <div
          className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2"
          role="alert"
        >
          <Text className="text-amber-200" size="sm">
            {bannerMessage}
          </Text>
        </div>
      )}
      <Textarea
        className="min-h-[4rem] border-zinc-600 bg-zinc-900 text-white placeholder:text-zinc-400 dark:border-zinc-600 dark:bg-zinc-900"
        disabled={isLoading}
        onChange={(e) => {
          onBannerClear();
          setValue(e.target.value);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        resizable={false}
        rows={3}
        value={value}
      />
      <div className="flex justify-end gap-2">
        {isLoading && <p className="text-xs text-zinc-400">{loadingMessage}</p>}
        <Button
          aria-label="Send"
          className="[&>[data-slot=icon]]:size-4"
          disabled={!value.trim() || isLoading}
          onClick={() => void handleSubmit(close)}
          outline
          size="xs"
          title="Send"
          type="button"
        >
          <ArrowUpIcon data-slot="icon" />
        </Button>
      </div>
    </div>
  );
};
